import { calculateHorizonBoundingBoxApi, getInterpretationsIdsFromHorizonApi } from '@core/core/api'
import type { GridBuildFormData } from '@core/core/types'
import type { CommonDomainType, ProjectId } from 'common/types'
import { useEffect, useState } from 'react'

import type {
  MapBuildingResult,
  OnUpdateGeometryData,
  OnUpdateKernelData,
  OnUpdateKrigingData,
  OnUpdateMinimalCurvatureData,
  OnUpdateMovingAverageData,
  OnUpdateSequentialConvergenceData,
  OnUpdateSourcesData,
  OnUpdateTargetData,
} from '../types'

export const useMapBuilding = (projectId: ProjectId): MapBuildingResult => {
  const [formData, setFormData] = useState<GridBuildFormData>({
    buildInfo: {
      name: '',
      sources: {
        interpretations2d: [],
        interpretations3d: [],
        horizons: [],
      },
      geometry: {
        xMin: 567900,
        yMin: 6746162,
        xMax: 584500,
        yMax: 6767328,
        xStep: 100,
        yStep: 100,
      },
      faults: [],
    },
    kernel: null,
    kriging: null,
    sequentialConvergence: null,
    minimalCurvature: null,
    movingAverage: null,
  })

  const updateKernelData: OnUpdateKernelData = callback => {
    if (typeof callback === 'function') {
      setFormData(prev => {
        const newKernel = callback(prev.kernel)
        if (newKernel) {
          return {
            ...prev,
            kernel: newKernel,
          }
        }
        return prev
      })
    }
  }

  const updateSourcesData: OnUpdateSourcesData = callback => {
    if (typeof callback === 'function') {
      setFormData(prev => {
        const newSources = callback(prev.buildInfo.sources)
        if (newSources) {
          return {
            ...prev,
            buildInfo: { ...prev.buildInfo, sources: newSources },
          }
        }
        return prev
      })
    }
  }

  const updateGeometryData: OnUpdateGeometryData = callback => {
    if (typeof callback === 'function') {
      setFormData(prev => {
        const newGeometry = callback(prev.buildInfo.geometry)
        if (newGeometry) {
          return {
            ...prev,
            buildInfo: { ...prev.buildInfo, geometry: newGeometry },
          }
        }
        return prev
      })
    }
  }

  const updateKrigingData: OnUpdateKrigingData = callback => {
    if (typeof callback === 'function') {
      setFormData(prev => {
        const newKriging = callback(prev.kriging)
        if (newKriging) {
          return {
            ...prev,
            kriging: { ...newKriging, buildInfo: { ...prev.buildInfo } },
          }
        }
        return prev
      })
    }
  }

  const updateSequentialConvergenceData: OnUpdateSequentialConvergenceData = callback => {
    if (typeof callback === 'function') {
      setFormData(prev => {
        const newSequentialConvergence = callback(prev.sequentialConvergence)
        if (newSequentialConvergence) {
          return {
            ...prev,
            sequentialConvergence: {
              ...newSequentialConvergence,
              buildInfo: { ...prev.buildInfo },
            },
          }
        }
        return prev
      })
    }
  }

  const updateTargetData: OnUpdateTargetData = callback => {
    if (typeof callback === 'function') {
      setFormData(prev => {
        const newTarget = callback(prev.buildInfo.name)
        return {
          ...prev,
          buildInfo: {
            ...prev.buildInfo,
            name: newTarget,
          },
        }
      })
    }
  }

  const updateFaultsData = (faultsData: { id: string; domain?: CommonDomainType }[]) => {
    const domainType =
      formData.buildInfo.sources.interpretations3d[0]?.domainType ??
      formData.buildInfo.sources.interpretations2d[0]?.domainType ??
      'TWT'
    const faults = faultsData.filter(({ domain }) => domain === domainType).map(({ id }) => id)
    setFormData(prev => ({ ...prev, buildInfo: { ...prev.buildInfo, faults } }))
  }

  useEffect(() => {
    const horizon2dIds = formData.buildInfo.sources.interpretations2d.map(({ id }) => id)
    const horizon3dIds = formData.buildInfo.sources.interpretations3d.map(({ id }) => id)
    const horizonIds = formData.buildInfo.sources.horizons.map(({ id }) => id)

    if (horizonIds.length) {
      getInterpretationsIdsFromHorizonApi(projectId, horizonIds).then(response => {
        const { horizon2dIds: apiHorizon2dIds, horizon3dIds: apiHorizon3dIds } = response
        horizon2dIds.push(...apiHorizon2dIds)
        horizon3dIds.push(...apiHorizon3dIds)
        if (horizon2dIds.length || horizon3dIds.length) {
          getHorizonsBoundingBox(horizon2dIds, horizon3dIds)
        }
      })
    }

    if (!horizonIds.length && (horizon2dIds.length || horizon3dIds.length)) {
      getHorizonsBoundingBox(horizon2dIds, horizon3dIds)
    }
  }, [formData.buildInfo.sources])

  const getHorizonsBoundingBox = async (horizon2dIds: string[], horizon3dIds: string[]) => {
    const geometry = await calculateHorizonBoundingBoxApi(horizon2dIds, horizon3dIds)
    setFormData(prev => ({
      ...prev,
      buildInfo: { ...prev.buildInfo, geometry },
    }))
  }

  const updateMinimalCurvatureData: OnUpdateMinimalCurvatureData = callback => {
    if (typeof callback === 'function') {
      setFormData(prev => {
        const newMinimalCurvature = callback(prev.minimalCurvature)
        if (newMinimalCurvature) {
          return {
            ...prev,
            minimalCurvature: newMinimalCurvature,
          }
        }
        return prev
      })
    }
  }

  const updateMovingAverageData: OnUpdateMovingAverageData = callback => {
    if (typeof callback === 'function') {
      setFormData(prev => {
        const newMovingAverage = callback(prev.movingAverage)
        if (newMovingAverage) {
          return {
            ...prev,
            movingAverage: newMovingAverage,
          }
        }
        return prev
      })
    }
  }

  return {
    formData,
    updateSourcesData,
    updateGeometryData,
    updateKernelData,
    updateKrigingData,
    updateSequentialConvergenceData,
    updateTargetData,
    updateFaultsData,
    updateMinimalCurvatureData,
    updateMovingAverageData,
  }
}
